import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import Grid from "../../../../../components/dialogs/Grid";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import {CancelButton} from "../../../../../components/button/CancelButton";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CollectionDto} from "../../../../../common/apis/collection/CollectionDto";
import {CollectionPermissionLevelDto} from "../../../../../common/apis/user/CollectionPermissionLevelDto";
import collectionsService from "../../collections/service/CollectionsService";
import userService from "../../../../../common/apis/user/UserApiClient";
import userGroupApiClient from "../../../../../common/apis/usergroup/UserGroupApiClient";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {UserGroupsTranslationKey} from "../UserGroupsTranslationKey";
import {invalidateFindAllUserGroupPermissionsQueryCache} from "../UserGroupQuery";

interface UserGroupPermissionCreateDialogProps {
    open: boolean;
    groupId: string;
    onClose: () => void;
}

export default function UserGroupPermissionCreateDialog(props: UserGroupPermissionCreateDialogProps) {
    const {open, groupId, onClose} = props;

    const queryClient = useQueryClient();

    const [collections, setCollections] = useState<CollectionDto[]>([]);
    const [permissionLevels, setPermissionLevels] = useState<CollectionPermissionLevelDto[]>([]);

    const [isUnsuccessfullyValidated, setUnsuccessfullyValidated] = useState<boolean>(false);
    const [collection, setCollection] = useState<CollectionDto | undefined>(undefined);
    const [permissionLevel, setPermissionLevel] = useState<CollectionPermissionLevelDto | undefined>(undefined);
    const [collectionError, setCollectionError] = useState<string | undefined>(undefined);
    const [permissionLevelError, setPermissionLevelError] = useState<string | undefined>(undefined);

    useEffect(() => {
        Promise.all([
            getCollections(),
            getPermissionLevels(),
        ]).then(([fetchedCollections, fetchedPermissionLevels]) => {
            setCollections(fetchedCollections);
            setPermissionLevels(fetchedPermissionLevels);
        });
    }, []);

    useEffect(() => {
        return () => {
            if (open) {
                setUnsuccessfullyValidated(false);
                setCollection(undefined);
                setCollectionError(undefined);
                setPermissionLevel(undefined);
                setPermissionLevelError(undefined);
            }
        };
    }, [open]);

    function getCollections(): Promise<CollectionDto[]> {
        return collectionsService.getAll().catch(() => []);
    }

    function getPermissionLevels(): Promise<CollectionPermissionLevelDto[]> {
        return userService.getAllPermissionLevels().catch(() => []);
    }

    function validateCollection(value: CollectionDto | undefined | null): boolean {
        if (value) {
            setCollectionError(undefined);
            return true;
        } else {
            setCollectionError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
    }

    function validatePermissionLevel(value: CollectionPermissionLevelDto | undefined | null): boolean {
        if (value) {
            setPermissionLevelError(undefined);
            return true;
        } else {
            setPermissionLevelError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
    }

    function isFormValid(): boolean {
        const isCollectionValid = validateCollection(collection);
        const isPermissionLevelValid = validatePermissionLevel(permissionLevel);
        return isCollectionValid && isPermissionLevelValid;
    }

    function onAdd() {
        setUnsuccessfullyValidated(true);
        if (isFormValid()) {
            userGroupApiClient.createPermission(groupId, collection!.code, permissionLevel!.code)
                .then(() => {
                    invalidateFindAllUserGroupPermissionsQueryCache(groupId, queryClient);
                    onClose();
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                })
                .catch((e) => {
                    Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), e);
                });
        }
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle id="scroll-dialog-title"
                         title={t(UserGroupsTranslationKey.NEW_PERMISSION)}
                         onDialogClosed={onClose}/>

            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <SingleSelectField<CollectionDto>
                            id="collection-field"
                            label={t(UserGroupsTranslationKey.PERMISSION_COLLECTION)}
                            errorMessage={collectionError}
                            value={collection ?? null}
                            onChange={(value) => {
                                isUnsuccessfullyValidated && validateCollection(value);
                                setCollection(value ?? undefined);
                            }}
                            options={collections}
                            getOptionLabel={(option: CollectionDto) => option.name}
                            required={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <SingleSelectField<CollectionPermissionLevelDto>
                            id="permission-level-field"
                            label={t(UserGroupsTranslationKey.PERMISSION_LEVEL)}
                            errorMessage={permissionLevelError}
                            value={permissionLevel ?? null}
                            onChange={(value) => {
                                isUnsuccessfullyValidated && validatePermissionLevel(value);
                                setPermissionLevel(value ?? undefined);
                            }}
                            options={permissionLevels}
                            getOptionLabel={(option: CollectionPermissionLevelDto) => t(option.translationKey)}
                            required={true}/>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <CancelButton onClick={onClose}/>
                <Button color={"primary"} variant={"contained"} autoFocus onClick={onAdd}>
                    {t(CommonTranslationKey.ADD)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
